// Routes and app shell (pages are code-split)
import { lazy, Suspense, useEffect, useState } from 'react';
import { createBrowserRouter, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { setCurrency } from './format';
import { setLanguage } from './i18n';
import { MeProvider, useMe, useMeQuery } from './session';
import { LanguageSwitch, Loading } from './ui';
import { MfaGate } from './security';
import { LogoMark } from './brand';
import { BillingBanner } from './payments';
import { Icon } from './icons';
import { ThemeSwitch } from './ThemePicker';
import { applyTheme } from './theme';

const page = (load: () => Promise<{ default: React.ComponentType }>) => {
  const C = lazy(load);
  return <Suspense fallback={<Loading />}><C /></Suspense>;
};

type Kind = 'shop' | 'fleet' | 'owner_operator';
type NavItem = { to: string; label: string; icon: string; roles?: ('admin' | 'advisor' | 'mechanic')[]; kinds?: Kind[]; labelFor?: Partial<Record<Kind, string>>; driver?: boolean; operator?: boolean };
// One app, three kinds of accounts: each sees only what fits how they work
const NAV: NavItem[] = [
  { to: '/', icon: 'home', label: 'nav.dashboard', labelFor: { owner_operator: 'nav.my_trucks' }, driver: true, operator: true },
  { to: '/ai', icon: 'ai', label: 'nav.ai', driver: true, operator: true },
  { to: '/receipts', icon: 'receipt', label: 'nav.receipts', kinds: ['fleet', 'owner_operator'], driver: true, operator: true },
  { to: '/costs', icon: 'costs', label: 'nav.costs', roles: ['admin', 'advisor'], kinds: ['fleet', 'owner_operator'], operator: true },
  { to: '/compliance', icon: 'compliance', label: 'nav.compliance', kinds: ['fleet', 'owner_operator'], operator: true },
  { to: '/work-orders', icon: 'board', label: 'nav.board', kinds: ['shop', 'fleet'], labelFor: { fleet: 'nav.repairs' } },
  { to: '/customers', icon: 'customers', label: 'nav.customers', roles: ['admin', 'advisor'], kinds: ['shop'] },
  { to: '/units', icon: 'units', label: 'nav.units', operator: true },
  { to: '/maintenance', icon: 'maintenance', label: 'nav.maintenance', operator: true },
  { to: '/orders', icon: 'orders', label: 'nav.orders', operator: true },
  { to: '/parts', icon: 'parts', label: 'nav.parts', kinds: ['shop', 'fleet'] },
  { to: '/time', icon: 'time', label: 'nav.time', kinds: ['shop', 'fleet'] },
  { to: '/reports', icon: 'reports', label: 'nav.reports', roles: ['admin', 'advisor'], kinds: ['shop', 'fleet'] },
  { to: '/settings', icon: 'settings', label: 'nav.settings', driver: true, operator: true }
];

function Shell() {
  const { t } = useTranslation();
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [loc.pathname]);
  const logout = async () => {
    await api.post('/auth/logout').catch(() => {});
    qc.clear();
    navigate('/login');
  };
  const navLabel = (n: NavItem) => t(n.labelFor?.[me.isOwnerOp ? 'owner_operator' : me.isFleet ? 'fleet' : 'shop'] ?? n.label);
  const items = NAV.filter(n => (me.role === 'driver' ? n.driver : me.asOperator ? n.operator : !n.roles || n.roles.includes(me.role as 'admin')) && (!n.kinds || n.kinds.some(k => me.has(k))));
  const initials = me.user.name.split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  return (
    <>
      <div className="topbar no-print">
        <button aria-label={t('nav.menu')} aria-expanded={open} onClick={() => setOpen(o => !o)}><Icon name="menu" size={24} /></button>
        <LogoMark /><b>{me.settings.shop_name}</b>
      </div>
      <div className="shell">
        <nav className={'side no-print' + (open ? ' open' : '')} aria-label={t('nav.menu')}>
          <div className="brand"><LogoMark /><span className="brand-text"><b>VegaPart</b><small>{me.settings.shop_name}</small></span></div>
          <div className="nav-links">
            {items.map(n => (
              <NavLink key={n.to} to={n.to} end={n.to === '/'} title={navLabel(n)} className={({ isActive }) => (isActive ? 'on' : '')}>
                <Icon name={n.icon} /><span>{navLabel(n)}</span>
              </NavLink>
            ))}
            {me.user.is_platform_admin && <NavLink to="/platform" title={t('nav.platform')}><Icon name="platform" /><span>{t('nav.platform')}</span></NavLink>}
          </div>
          <div className="sp" />
          <div className="side-foot">
            <ThemeSwitch />
            <div className="who" title={me.user.name}><span className="avatar">{initials || '·'}</span><span><b>{me.user.name}</b><small>{t('role.' + me.role)}</small></span></div>
            <LanguageSwitch signedIn />
            {me.memberships.length > 1 && <NavLink to="/pick-shop" title={t('auth.switch_shop')}><Icon name="swap" /><span>{t('auth.switch_shop')}</span></NavLink>}
            <a href="#" title={t('nav.logout')} onClick={e => { e.preventDefault(); void logout(); }}><Icon name="logout" /><span>{t('nav.logout')}</span></a>
          </div>
        </nav>
        <main className="main"><BillingBanner /><Outlet /></main>
      </div>
    </>
  );
}

/** Loads the session; sends to /login or /pick-shop when needed */
function Authed() {
  const q = useMeQuery();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!q.data?.settings) return;
    setCurrency(q.data.settings.currency);
    applyTheme(q.data.user.ui_theme ?? null);
    // Personal language first, then the company default
    void setLanguage(q.data.user.language ?? q.data.settings.language).then(() => setReady(true));
  }, [q.data]);
  if (q.isError) return <Navigate to="/login" replace />;
  if (!q.data) return <div className="boot">…</div>;
  // Company policy or VegaPart staff: two-step sign-in must be done before anything else
  if (q.data.mfa?.required && !q.data.mfa.session_verified) return <MfaGate enabled={q.data.mfa.enabled} />;
  if (!q.data.tenant_id) return <Navigate to={q.data.user.is_platform_admin && !q.data.memberships.length ? '/platform' : '/pick-shop'} replace />;
  if (!ready) return <div className="boot">…</div>;
  return <MeProvider me={q.data}><Shell /></MeProvider>;
}

export const router = createBrowserRouter([
  { path: '/login', element: page(() => import('./pages/Auth').then(m => ({ default: m.Login }))) },
  { path: '/signup', element: page(() => import('./pages/Auth').then(m => ({ default: m.Signup }))) },
  { path: '/forgot', element: page(() => import('./pages/Auth').then(m => ({ default: m.Forgot }))) },
  { path: '/reset', element: page(() => import('./pages/Auth').then(m => ({ default: m.Reset }))) },
  // The shop's customer pays an invoice here (no sign-in)
  { path: '/pay/:token', element: page(() => import('./pages/Pay')) },
  { path: '/pick-shop', element: page(() => import('./pages/Auth').then(m => ({ default: m.PickShop }))) },
  // VegaPart staff console (works with or without a shop account)
  { path: '/platform', element: page(() => import('./pages/Platform')) },
  { path: '/platform/accounts', element: page(() => import('./pages/Platform').then(m => ({ default: m.PlatformAccounts }))) },
  { path: '/platform/:id', element: page(() => import('./pages/Platform').then(m => ({ default: m.PlatformOrder }))) },
  {
    path: '/', element: <Authed />, children: [
      { index: true, element: page(() => import('./pages/Dashboard')) },
      { path: 'work-orders', element: page(() => import('./pages/Board')) },
      { path: 'work-orders/:id', element: page(() => import('./pages/WorkOrder')) },
      { path: 'work-orders/:id/invoice', element: page(() => import('./pages/Invoice')) },
      { path: 'customers', element: page(() => import('./pages/Customers')) },
      { path: 'customers/:id', element: page(() => import('./pages/Customer')) },
      { path: 'units', element: page(() => import('./pages/Units')) },
      { path: 'units/:id', element: page(() => import('./pages/Unit')) },
      { path: 'maintenance', element: page(() => import('./pages/Maintenance')) },
      { path: 'parts', element: page(() => import('./pages/Parts')) },
      { path: 'time', element: page(() => import('./pages/Time')) },
      { path: 'ai', element: page(() => import('./pages/AiNew')) },
      { path: 'orders', element: page(() => import('./pages/Orders')) },
      { path: 'orders/:id', element: page(() => import('./pages/Order')) },
      { path: 'ai/:id', element: page(() => import('./pages/AiResult')) },
      { path: 'reports', element: page(() => import('./pages/Reports')) },
      { path: 'receipts', element: page(() => import('./pages/Receipts')) },
      { path: 'receipts/:id', element: page(() => import('./pages/Receipts').then(m => ({ default: m.ReceiptReview }))) },
      { path: 'costs', element: page(() => import('./pages/Costs')) },
      { path: 'compliance', element: page(() => import('./pages/Compliance')) },
      { path: 'dvir/new', element: page(() => import('./pages/Compliance').then(m => ({ default: m.DvirNew }))) },
      { path: 'units/:id/dot-file', element: page(() => import('./pages/Compliance').then(m => ({ default: m.DotFilePage }))) },
      { path: 'settings', element: page(() => import('./pages/Settings')) },
      { path: '*', element: <Navigate to="/" replace /> }
    ]
  }
]);
